import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Shield, AlertTriangle, Globe, Monitor, Clock, CheckCircle, XCircle,
  MapPin, Wifi, RefreshCw, Trash2, Loader2, Eye, Lock, Skull,
  Activity, ChevronDown, ChevronUp, Smartphone, Laptop,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const THREAT_CONFIG = {
  critical: { label: "Critique", color: "#ef4444", bg: "#ef444415", icon: Skull },
  high: { label: "Élevé", color: "#f59e0b", bg: "#f59e0b15", icon: AlertTriangle },
  medium: { label: "Moyen", color: "#f97316", bg: "#f9731615", icon: Shield },
  low: { label: "Faible", color: "#22c55e", bg: "#22c55e15", icon: CheckCircle },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}j`;
};

export default function SecurityAdminTab() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("__all__");
  const [expanded, setExpanded] = useState({});
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filter !== "__all__" ? `?threat=${filter}` : "";
      const [logsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/security/logs${params}`, { headers }),
        fetch(`${API_URL}/api/security/stats`, { headers }),
      ]);
      if (logsRes.ok) setLogs(await logsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [filter]);

  const clearLogs = async () => {
    if (!window.confirm("Supprimer tous les logs de sécurité ?")) return;
    await fetch(`${API_URL}/api/security/logs`, { method: "DELETE", headers });
    fetchData();
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: "Total connexions", value: stats.total, icon: Activity, color: "#6366f1" },
            { label: "Aujourd'hui", value: stats.today_total, icon: Clock, color: "#3b82f6" },
            { label: "Échecs", value: stats.failed, icon: XCircle, color: "#f97316" },
            { label: "Échecs aujourd'hui", value: stats.today_failed, icon: AlertTriangle, color: "#f59e0b" },
            { label: "Alertes critiques", value: stats.critical, icon: Skull, color: "#ef4444" },
            { label: "Cibles admin", value: stats.admin_targets, icon: Shield, color: "#dc2626" },
            { label: "IPs suspectes", value: stats.unique_attack_ips, icon: Globe, color: "#8b5cf6" },
          ].map(s => (
            <Card key={s.label} className="overflow-hidden">
              <div className="h-1" style={{ background: s.color }} />
              <CardContent className="py-3 px-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${s.color}12` }}>
                    <s.icon className="h-4 w-4" style={{ color: s.color }} />
                  </div>
                  <div>
                    <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
                    <p className="text-[9px] text-muted-foreground leading-tight">{s.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Shield className="h-5 w-5 text-red-500" /> Journal de sécurité
        </h2>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tous les niveaux</SelectItem>
              <SelectItem value="critical">🔴 Critique</SelectItem>
              <SelectItem value="high">🟡 Élevé</SelectItem>
              <SelectItem value="medium">🟠 Moyen</SelectItem>
              <SelectItem value="low">🟢 Faible</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-1">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={clearLogs} className="gap-1 text-destructive">
            <Trash2 className="h-3.5 w-3.5" /> Vider
          </Button>
        </div>
      </div>

      {/* Logs */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : logs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-muted-foreground">
            <Shield className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p className="font-medium">Aucun log de sécurité</p>
            <p className="text-sm mt-1">Les tentatives de connexion apparaîtront ici</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {logs.map(log => {
            const tc = THREAT_CONFIG[log.threat_level] || THREAT_CONFIG.low;
            const ThreatIcon = tc.icon;
            const isExpanded = expanded[log.id];

            return (
              <Card key={log.id} className="overflow-hidden transition-all hover:shadow-md"
                style={{ borderLeft: `3px solid ${tc.color}` }}>
                <CardContent className="p-0">
                  {/* Main Row */}
                  <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                    onClick={() => setExpanded(p => ({ ...p, [log.id]: !p[log.id] }))}>
                    {/* Status icon */}
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: tc.bg }}>
                      {log.success ? <CheckCircle className="h-4 w-4 text-green-500" /> : <ThreatIcon className="h-4 w-4" style={{ color: tc.color }} />}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{log.email}</span>
                        <Badge variant="outline" className="text-[9px] h-4 px-1.5" style={{ borderColor: tc.color, color: tc.color }}>{tc.label}</Badge>
                        {log.is_admin_target && <Badge className="text-[9px] h-4 px-1.5 bg-red-500/10 text-red-500 border-red-300" variant="outline">ADMIN</Badge>}
                        {log.is_vpn && <Badge className="text-[9px] h-4 px-1.5 bg-purple-500/10 text-purple-500 border-purple-300" variant="outline">🛡 VPN/Proxy</Badge>}
                        {log.success ? (
                          <span className="text-[10px] text-green-500 font-medium">✓ Succès</span>
                        ) : (
                          <span className="text-[10px] text-red-500 font-medium">✗ Échec</span>
                        )}
                        {log.screenshot && <Badge className="text-[9px] h-4 px-1.5 bg-blue-500/10 text-blue-500 border-blue-300" variant="outline">📸 Capture</Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Globe className="h-3 w-3" />{log.ip}</span>
                        {log.hostname && log.hostname !== "Unknown" && <span className="flex items-center gap-1"><Monitor className="h-3 w-3" />{log.hostname}</span>}
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{log.geo?.city}, {log.geo?.country}</span>
                        <span className="flex items-center gap-1"><Laptop className="h-3 w-3" />{log.browser} / {log.os}</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{timeAgo(log.created_at)}</span>
                      </div>
                    </div>

                    {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 border-t border-border/30 bg-muted/20">
                      {/* Screenshot */}
                      {log.screenshot && (
                        <div className="pt-3 mb-4">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-2">📸 Capture d'écran de l'attaquant</p>
                          <div className="rounded-xl overflow-hidden border-2 border-red-200 shadow-lg max-w-2xl">
                            <img src={log.screenshot} alt="Security capture" className="w-full h-auto" />
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Adresse IP</p>
                          <p className="text-sm font-mono font-medium">{log.ip}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Hostname (Reverse DNS)</p>
                          <p className="text-sm font-mono">{log.hostname || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Localisation</p>
                          <p className="text-sm">{log.geo?.city}, {log.geo?.region}, {log.geo?.country}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Fournisseur (ISP)</p>
                          <p className="text-sm">{log.geo?.isp}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Organisation</p>
                          <p className="text-sm">{log.geo?.org || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">ASN</p>
                          <p className="text-sm font-mono">{log.geo?.as || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">VPN / Proxy / Hosting</p>
                          <p className="text-sm">{log.is_vpn ? <span className="text-red-500 font-semibold">⚠️ Oui - VPN/Proxy détecté</span> : <span className="text-green-500">Non</span>}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Appareil mobile</p>
                          <p className="text-sm">{log.is_mobile ? "📱 Oui" : "💻 Non"}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Fuseau horaire</p>
                          <p className="text-sm">{log.geo?.timezone || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Coordonnées GPS</p>
                          <p className="text-sm font-mono">{log.geo?.lat}, {log.geo?.lon}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Navigateur / OS</p>
                          <p className="text-sm">{log.browser} sur {log.os}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Raison</p>
                          <p className="text-sm font-medium" style={{ color: log.success ? "#22c55e" : "#ef4444" }}>{log.reason}</p>
                        </div>
                      </div>

                      {/* Client Fingerprint */}
                      {log.client_info && Object.keys(log.client_info).length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border/30">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-2">🖥️ Empreinte machine</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {log.client_info.platform && <div><p className="text-[9px] text-muted-foreground/50 font-bold">Plateforme</p><p className="text-xs font-mono">{log.client_info.platform}</p></div>}
                            {log.client_info.screen && <div><p className="text-[9px] text-muted-foreground/50 font-bold">Écran</p><p className="text-xs font-mono">{log.client_info.screen}</p></div>}
                            {log.client_info.localIp && <div><p className="text-[9px] text-muted-foreground/50 font-bold">IP Locale (LAN)</p><p className="text-xs font-mono text-orange-500 font-semibold">{log.client_info.localIp}</p></div>}
                            {log.client_info.language && <div><p className="text-[9px] text-muted-foreground/50 font-bold">Langue</p><p className="text-xs font-mono">{log.client_info.language}</p></div>}
                            {log.client_info.timezone && <div><p className="text-[9px] text-muted-foreground/50 font-bold">Timezone client</p><p className="text-xs font-mono">{log.client_info.timezone}</p></div>}
                            {log.client_info.hardwareConcurrency > 0 && <div><p className="text-[9px] text-muted-foreground/50 font-bold">CPU Cores</p><p className="text-xs font-mono">{log.client_info.hardwareConcurrency}</p></div>}
                            {log.client_info.deviceMemory && log.client_info.deviceMemory !== "Unknown" && <div><p className="text-[9px] text-muted-foreground/50 font-bold">RAM</p><p className="text-xs font-mono">{log.client_info.deviceMemory} GB</p></div>}
                            {log.client_info.gpuRenderer && <div className="col-span-2"><p className="text-[9px] text-muted-foreground/50 font-bold">GPU</p><p className="text-xs font-mono">{log.client_info.gpuRenderer}</p></div>}
                            {log.client_info.colorDepth && <div><p className="text-[9px] text-muted-foreground/50 font-bold">Couleurs</p><p className="text-xs font-mono">{log.client_info.colorDepth}-bit</p></div>}
                            {log.client_info.pixelRatio && <div><p className="text-[9px] text-muted-foreground/50 font-bold">Pixel Ratio</p><p className="text-xs font-mono">{log.client_info.pixelRatio}x</p></div>}
                            {log.client_info.connectionType && log.client_info.connectionType !== "Unknown" && <div><p className="text-[9px] text-muted-foreground/50 font-bold">Connexion</p><p className="text-xs font-mono">{log.client_info.connectionType}</p></div>}
                            {log.client_info.maxTouchPoints > 0 && <div><p className="text-[9px] text-muted-foreground/50 font-bold">Touch Points</p><p className="text-xs font-mono">{log.client_info.maxTouchPoints}</p></div>}
                          </div>
                        </div>
                      )}

                      <div className="mt-4 pt-4 border-t border-border/30">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">User-Agent complet</p>
                        <p className="text-xs font-mono bg-muted/50 rounded-lg p-2 break-all">{log.user_agent}</p>
                      </div>
                      <div className="mt-3">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-bold mb-1">Date / Heure</p>
                        <p className="text-sm">{new Date(log.created_at).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "long" })}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
